import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { userApi } from "../repositories/user";
import { setUser as setReduxUser, setSubscriptionStatus, setLoading as setUserLoading, setError as setUserError, type Permission, type Purchase } from "../store/slices/userSlice";
import type { AppDispatch } from "../store";

interface User {
  id: number;
  fullName: string;
  email: string;
  role: string;
  type: string;
  labelId: number;
  createdAt: string;
  lastLogin: string | null;
  profilePicture: string | null;
  Label: {
    name: string;
    type?: string;
    availableDSPs: string[];
    availableAnalyticsDSPs: string[];
  };
  permissions?: Permission[];
  purchase?: Purchase;
  hasDismissedDraftPitchReminder?: boolean;
  organization?: {
    id: string;
    name: string;
  };
  subscription?: {
    plan: string;
    billing: {
      amount: number;
      interval: string;
    };
    status: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    nextPaymentDate: string;
    nextPaymentAmount: number;
    rosterLimit: number;
    rosterUsed: number;
    aimsAccess: boolean;
    apiAccess: boolean;
  };
}

export const useUser = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    setError(null);
    dispatch(setUserLoading(true));
    try {
      const response = await userApi.getProfile();

      if (!response?.data?.success) {
        throw new Error(response?.data?.error || "Failed to fetch profile");
      }
      const userData = response.data.data;
      setUser(userData);

      // Transform permissions: map permission_name to type (extract part before dot)
      const transformedPermissions = (userData.permissions || []).map((perm: any) => ({
        ...perm,
        type: perm.type || (perm.permission_name ? perm.permission_name.split('.')[0] : ''),
        used: perm.used ?? null,
        limit: perm.limit ?? null,
        details: perm.details || [],
        permission_id: perm.permission_id,
      }));

      // Sync labelId and labelName to auth localStorage for axios interceptor access
      try {
        const authData = localStorage.getItem("auth");
        if (authData) {
          const auth = JSON.parse(authData);
          if (auth.user) {
            // Update labelId and labelName from the /me API response
            if (userData.labelId) {
              auth.user.labelId = userData.labelId;
            }
            if (userData.Label?.name) {
              auth.user.labelName = userData.Label.name;
            }
            if (userData.Label?.type) {
              auth.user.labelType = userData.Label.type;
            }
            localStorage.setItem("auth", JSON.stringify(auth));
          }
        }
      } catch (error) {
        console.error("Error updating labelId and labelName in auth:", error);
      }

      // Sync user data to Redux store
      dispatch(setReduxUser({
        id: userData.id,
        fullName: userData.fullName,
        email: userData.email,
        role: userData.role,
        userType: userData.type,
        labelId: userData.labelId,
        createdAt: userData.createdAt,
        lastLogin: userData.lastLogin,
        profilePicture: userData.profilePicture,
        Label: userData.Label,
        organization: userData.organization || null,
        permissions: transformedPermissions,
        purchase: userData.purchase || null,
        hasDismissedDraftPitchReminder: userData.hasDismissedDraftPitchReminder || false,
      }));

      // Fetch subscription status if user is a subscription user
      if (userData.type === "subscription") {
        try {
          const subscriptionResponse = await userApi.getSubscriptionDetails();
          if (subscriptionResponse?.data?.data?.status) {
            dispatch(setSubscriptionStatus(subscriptionResponse.data.data.status));
          }
        } catch (error) {
          console.error("Error fetching subscription status:", error);
          // Set to empty string to indicate fetch was attempted but failed
          dispatch(setSubscriptionStatus("error"));
        }
      } else {
        // For non-subscription users, set status to 'not_applicable' to prevent waiting
        dispatch(setSubscriptionStatus("not_applicable"));
      }

      return userData;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to fetch profile";
      console.error("Error fetching user profile:", error);
      setError(errorMessage);
      dispatch(setUserError(errorMessage));
      return null;
    } finally {
      setLoading(false);
      dispatch(setUserLoading(false));
    }
  }, [dispatch]);

  return { user, loading, error, fetchProfile };
};
